import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, isTransientFailure } from '@wundo/client/api.ts';
import type { MeetingPageOfflineRecording } from '@wundo/shared/meetingPage.ts';
import {
  startOfflineRecording,
  stopOfflineRecording,
  uploadOfflineRecordingChunk,
} from './meetingPage.ts';
import { keepSystemAwake } from './keepSystemAwake.ts';
import { openChosenMicrophone } from './openChosenMicrophone.ts';
import { reportError } from './reportError.ts';
import { isMicrophoneBlocked } from './roleplayCall.ts';

/*
 * One upload every half minute. Short enough that a laptop closing costs a
 * sentence rather than a meeting, long enough that an hour of talking is a
 * hundred-odd requests instead of thousands.
 */
const CHUNK_MS = 30_000;
/* Nobody means to record a whole afternoon. At three hours the page asks. */
const CAP_MS = 3 * 60 * 60 * 1000;
/* How long that question stands before the recording closes itself. */
const CAP_GRACE_MS = 5 * 60 * 1000;
/*
 * What the backend knows how to read back: one MediaRecorder session's chunks
 * concatenate into a valid file, and the backend joins the sessions a reload
 * leaves behind. Chrome, Edge and Safari 18.4+ all record it.
 */
const RECORDING_MIME = 'audio/webm;codecs=opus';

/**
 * What the page shows about a microphone recording of this meeting.
 * `interrupted` is not a server state: the server still says `recording`, and
 * nothing on this device holds a microphone any more (the page was reloaded).
 */
export type OfflineRecordingState =
  | 'idle'
  | 'recording'
  | 'interrupted'
  | 'processing'
  | 'done'
  | 'failed';

interface Session {
  stream: MediaStream;
  recorder: MediaRecorder;
  /** Undoes keepSystemAwake: the computer may idle-sleep again. */
  letSleep: () => void;
}

export interface OfflineRecording {
  state: OfflineRecordingState;
  /** Whether this browser can record at all — Safari before 18.4 cannot record webm. */
  supported: boolean;
  /** Seconds since the running recording began, for the pill's clock. */
  elapsed: number;
  /** When it began, for "sinds 14:05". Null when nothing is running. */
  startedAt: Date | null;
  /**
   * The microphone the audio is coming from, read off the open stream rather
   * than the picked device id, so it is right when the browser chose one.
   */
  deviceLabel: string | null;
  starting: boolean;
  stopping: boolean;
  /** The last start did not happen. */
  startFailed: boolean;
  /** A chunk was lost for good. The recording carries on without it. */
  chunkLost: boolean;
  /** Three hours in, asking whether to keep going. */
  capReached: boolean;
  /**
   * Begin, or pick an interrupted recording back up; a null device takes any.
   * `onStarted` closes the asking dialog once audio flows, so a failed start
   * leaves it standing. `onStale` closes it when the page was behind the server.
   */
  start: (deviceId: string | null, onStarted?: () => void, onStale?: () => void) => void;
  stop: () => void;
  /** Answer to the three-hour question: carry on for another three. */
  keepGoing: () => void;
}

export interface OfflineRecordingRequest {
  userId: string;
  meetingId: string;
  /** The meeting page's own account of where this recording stands. */
  offline: MeetingPageOfflineRecording;
  /** The server's state moved; ask the page for itself again. */
  onChanged: () => void;
}

/** Whether this browser can record what the backend knows how to read. */
function canRecordHere(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(RECORDING_MIME)
  );
}

/**
 * Recording a physical meeting through the microphone of the device the user is
 * sitting at. The recording lives on the server: this holds the microphone and
 * ships half-minute chunks, and a reloaded page resumes numbering from the
 * server's count. Nothing is buffered for later in a tab that may close.
 */
export function useOfflineRecording({
  userId,
  meetingId,
  offline,
  onChanged,
}: OfflineRecordingRequest): OfflineRecording {
  const session = useRef<Session | null>(null);

  const [live, setLive] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null);
  const [localStart, setLocalStart] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [startFailed, setStartFailed] = useState(false);

  const wakeLock = useScreenWakeLock(live);
  const chunks = useChunkUploads(userId, meetingId);
  const cap = useRecordingCap(live);

  /*
   * The server's word while it has one, this device's while the page has not
   * been read again yet. They say the same thing a moment later.
   */
  const startedIso =
    localStart ?? (offline.status === 'recording' ? offline.started_at : null);
  const startedMs = startedIso === null ? null : Date.parse(startedIso);
  const elapsed = useElapsedSeconds(live, startedMs);
  useWarnBeforeUnload(live);

  const { queue, beginAt, clearLost, uploaded } = chunks;
  const { hold, release } = wakeLock;
  const { arm, disarm } = cap;

  const start = useCallback(
    (deviceId: string | null, onStarted?: () => void, onStale?: () => void) => {
      if (session.current !== null) return;
      setStarting(true);
      setStartFailed(false);
      clearLost();

      void (async () => {
        let stream: MediaStream | null = null;
        try {
          stream = await openChosenMicrophone(deviceId, (id) =>
            id === null ? true : { deviceId: { exact: id } },
          );

          /*
           * The server first: it decides the numbering, and a recorder started
           * before it would have to throw away what it caught if the meeting
           * turns out to be one Wundo may not record.
           */
          const state = await startOfflineRecording(userId, meetingId);
          beginAt(state.next_chunk_index);

          const recorder = new MediaRecorder(stream, { mimeType: RECORDING_MIME });
          recorder.ondataavailable = (event) => queue(event.data);
          recorder.start(CHUNK_MS);
          session.current = { stream, recorder, letSleep: keepSystemAwake() };

          setDeviceLabel(stream.getAudioTracks()[0]?.label || null);
          setLocalStart(state.started_at ?? new Date().toISOString());
          arm();
          setLive(true);
          void hold();
          onStarted?.();
          onChanged();
        } catch (caught) {
          stream?.getTracks().forEach((track) => track.stop());
          /* The page offered to record while a recording was still processing. */
          if (caught instanceof ApiError && caught.status === 422) {
            onStale?.();
            onChanged();
            return;
          }
          /* A refused microphone is the user's answer, not a fault to report. */
          if (!isMicrophoneBlocked(caught)) {
            reportError(caught, { what: 'offline-recording-start', meetingId });
          }
          setStartFailed(true);
        } finally {
          setStarting(false);
        }
      })();
    },
    [userId, meetingId, onChanged, queue, beginAt, clearLost, arm, hold],
  );

  const stop = useCallback(() => {
    const current = session.current;
    session.current = null;
    setLive(false);
    setDeviceLabel(null);
    disarm();
    setStopping(true);
    release();
    current?.letSleep();

    void (async () => {
      try {
        if (current !== null) await closeSession(current);
        /* Everything recorded is on the server before it is asked to read it. */
        await uploaded();
        await stopOfflineRecording(userId, meetingId);
      } catch (caught) {
        /*
         * The server still says `recording`, so the page comes back as an interrupted
         * recording.
         */
        reportError(caught, { what: 'offline-recording-stop', meetingId });
      } finally {
        setLocalStart(null);
        setStopping(false);
        onChanged();
      }
    })();
  }, [userId, meetingId, onChanged, release, disarm, uploaded]);

  /*
   * The answer nobody gave to the three-hour question: a recording nobody is at closes
   * itself.
   */
  useEffect(() => {
    if (!cap.reached) return;

    const timer = setTimeout(() => stop(), CAP_GRACE_MS);
    return () => clearTimeout(timer);
  }, [cap.reached, stop]);

  /*
   * Leaving the page lets go of the microphone: a recording light that stays on
   * after the page is gone is worse than a recording that has to be resumed.
   * The server keeps its own count, so this comes back as `interrupted`.
   */
  useEffect(
    () => () => {
      const current = session.current;
      session.current = null;
      if (current !== null) {
        if (current.recorder.state !== 'inactive') current.recorder.stop();
        current.stream.getTracks().forEach((track) => track.stop());
        current.letSleep();
      }
      release();
    },
    [release],
  );

  return {
    state: recordingState(offline.status, live, stopping),
    supported: canRecordHere(),
    elapsed,
    startedAt: startedMs === null ? null : new Date(startedMs),
    deviceLabel,
    starting,
    stopping,
    startFailed,
    chunkLost: chunks.lost,
    capReached: cap.reached,
    start,
    stop,
    keepGoing: arm,
  };
}

/*
 * The last, part-length chunk only exists once the recorder is asked to stop,
 * so the stream stays open until it has been handed over.
 */
async function closeSession(current: Session): Promise<void> {
  if (current.recorder.state !== 'inactive') {
    await new Promise<void>((resolve) => {
      current.recorder.onstop = () => resolve();
      current.recorder.stop();
    });
  }
  current.stream.getTracks().forEach((track) => track.stop());
}

/**
 * Keeps the screen on while recording. The browser drops the lock whenever the
 * tab is hidden and does not hand it back, so it is asked for again every time
 * the page is looked at.
 */
function useScreenWakeLock(live: boolean) {
  const lock = useRef<WakeLockSentinel | null>(null);

  const hold = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      lock.current = await navigator.wakeLock.request('screen');
    } catch {
      /* A screen that dims is a nuisance, never a reason to stop recording. */
    }
  }, []);

  const release = useCallback(() => {
    const held = lock.current;
    lock.current = null;
    void held?.release().catch(() => {
      /* Already gone — the browser drops it whenever the tab is hidden. */
    });
  }, []);

  useEffect(() => {
    if (!live) return;

    const regain = () => {
      if (document.visibilityState === 'visible' && lock.current === null) {
        void hold();
      }
    };
    document.addEventListener('visibilitychange', regain);
    return () => document.removeEventListener('visibilitychange', regain);
  }, [live, hold]);

  return { hold, release };
}

/**
 * Uploads chunks one after the other. They are numbered, so order does not
 * decide what the file becomes, but stop may only be sent once the last one
 * has landed. A failed chunk is retried once if the failure looks passing.
 */
function useChunkUploads(userId: string, meetingId: string) {
  /* The number the next chunk goes up under. */
  const nextIndex = useRef(0);
  const uploads = useRef<Promise<void>>(Promise.resolve());
  const [lost, setLost] = useState(false);

  const queue = useCallback(
    (blob: Blob) => {
      if (blob.size === 0) return;
      const index = nextIndex.current;
      nextIndex.current += 1;
      const lose = (caught: unknown) => {
        reportError(caught, { what: 'offline-recording-chunk', meetingId, index });
        setLost(true);
      };
      uploads.current = uploads.current.then(async () => {
        try {
          await uploadOfflineRecordingChunk(userId, meetingId, index, blob);
        } catch (caught) {
          /* A refused session answers the same the second time; only a hiccup earns a retry. */
          if (!isTransientFailure(caught)) {
            lose(caught);
            return;
          }
          try {
            await uploadOfflineRecordingChunk(userId, meetingId, index, blob);
          } catch (retryCaught) {
            lose(retryCaught);
          }
        }
      });
    },
    [userId, meetingId],
  );

  const beginAt = useCallback((index: number) => {
    nextIndex.current = index;
  }, []);
  const clearLost = useCallback(() => setLost(false), []);
  const uploaded = useCallback(() => uploads.current, []);

  return { queue, beginAt, clearLost, uploaded, lost };
}

/* The three-hour question: armed when a recording starts, and again on "keep going". */
function useRecordingCap(live: boolean) {
  const [capAt, setCapAt] = useState<number | null>(null);
  const [reached, setReached] = useState(false);

  useEffect(() => {
    if (!live || capAt === null) return;

    const timer = setTimeout(() => setReached(true), Math.max(0, capAt - Date.now()));
    return () => clearTimeout(timer);
  }, [live, capAt]);

  const arm = useCallback(() => {
    setReached(false);
    setCapAt(Date.now() + CAP_MS);
  }, []);
  const disarm = useCallback(() => {
    setReached(false);
    setCapAt(null);
  }, []);

  return { reached, arm, disarm };
}

/* The clock on the pill, and nothing else: one tick a second while running. */
function useElapsedSeconds(live: boolean, startedMs: number | null): number {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!live || startedMs === null) return;

    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [live, startedMs]);

  return elapsed;
}

/*
 * Only the browser's own dialog can stand in front of a closing tab. A link
 * followed inside the app is a navigation the page itself blocks and asks about.
 */
function useWarnBeforeUnload(live: boolean): void {
  useEffect(() => {
    if (!live) return;

    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [live]);
}

/**
 * What the page is looking at, from the server's word and this device's. This
 * device wins while it holds a microphone, and a stop reads as `processing`
 * until the server has been told, rather than as `interrupted`.
 */
function recordingState(
  status: MeetingPageOfflineRecording['status'],
  live: boolean,
  stopping: boolean,
): OfflineRecordingState {
  if (live) return 'recording';
  if (stopping) return 'processing';
  return status === 'recording' ? 'interrupted' : status;
}
